import { Controller } from '@hotwired/stimulus';
import { Turbo } from '@hotwired/turbo-rails';
import bootstrap from 'bootstrap';

export default class extends Controller {
  static targets = ['modelSelect', 'hiddenColumnsConfig', 'joinsConfig', 'selectedColumnsList', 'selectedColumnRow', 'calculatedColumnFrame', 'groupingCard', 'aggregateRow'];
  static values = { filterTypesMap: Object };

  modelChanged() {
    this.reloadModelContext();
  }

  reportTypeChanged() {
    this.reloadModelContext();
  }

  reloadModelContext() {
    const selectedModel = this.hasModelSelectTarget ? this.modelSelectTarget.value : '';
    if (!selectedModel) return;

    const params = new URLSearchParams({ report_model: selectedModel });
    const reportType = this.currentReportType();
    if (reportType) params.set('report_type', reportType);

    // The General tab (model/name/type) lives outside any turbo frame so it survives the reload;
    // the Related Tables / Report Columns / Filters frames are replaced via the turbo stream.
    fetch(`/rad_reports/model_context?${params.toString()}`, {
      headers: { Accept: 'text/vnd.turbo-stream.html' }
    })
      .then((response) => response.text())
      .then((html) => Turbo.renderStreamMessage(html));
  }

  currentReportType() {
    const checked = this.element.querySelector('input[name="rad_report[report_type]"]:checked');
    return checked ? checked.value : '';
  }

  filterColumnChanged(event) {
    const select = event.target;
    const selectedOption = select.options[select.selectedIndex];
    const columnType = selectedOption.dataset.columnType;
    const isForeignKey = selectedOption.dataset.isForeignKey === 'true';
    const isEnum = selectedOption.dataset.isEnum === 'true';
    const card = select.closest('.filter-card');

    const labelInput = card.querySelector('input[name="rad_report[filters][][label]"]');
    if (labelInput && select.value) {
      const columnName = select.value.split('.').pop();
      labelInput.value = this.generateLabel(columnName);
    }

    const typeSelect = card.querySelector('select[name="rad_report[filters][][type]"]');
    if (typeSelect && columnType) {
      let filterTypes = this.filterTypesMapValue[columnType] || this.filterTypesMapValue['string'];

      if (isEnum) {
        filterTypes = [['Enum', 'RadSearch::EnumFilter']];
      } else if (!isForeignKey) {
        filterTypes = filterTypes.filter(filter => filter[1] !== 'RadSearch::SearchFilter');
      }

      this.resetTomSelect(typeSelect, filterTypes);
    }
  }

  resetTomSelect(selectElement, filterTypes) {
    const tomselect = selectElement.tomselect;
    tomselect.clear();
    tomselect.clearOptions();
    filterTypes.forEach((filter) => {
      tomselect.addOption({ value: filter[1], text: filter[0] });
    });
    tomselect.refreshOptions(false);
    tomselect.setValue(filterTypes[0][1]);
  }

  updateColumnLabel(event) {
    const input = event.target;
    const row = input.closest('tr');
    row.dataset.columnLabel = input.value;
  }

  // Sortable toggle. Measures submit via a static hidden input, so keep it in sync; detail columns
  // and grouping cards are read from the checkbox at submit time.
  toggleSortable(event) {
    const checkbox = event.target;
    const row = checkbox.closest('tr, .grouping-card, .aggregate-row');
    const sortableInput = row?.querySelector('input[type="hidden"][name*="[sortable]"]');
    if (sortableInput) sortableInput.value = checkbox.checked ? '1' : '0';
  }

  updateGroupingHidden(event) {
    event.target.closest('.grouping-card').dataset.columnHidden = event.target.checked;
  }

  removeGrouping(event) {
    event.currentTarget.closest('.grouping-card')?.remove();
  }

  // A display field is only valid if its table's primary key is a current group-by key (Postgres
  // functional dependency). Disable field options that aren't grounded by a PK group key.
  filterFieldMenu() {
    const grounded = new Set();
    this.element
      .querySelectorAll('#group-keys-list [data-report-builder-target="groupingCard"]')
      .forEach((card) => {
        if (card.dataset.columnIsPk === 'true' && card.dataset.columnPath) {
          grounded.add(card.dataset.columnPath);
        }
      });

    this.element.querySelectorAll('.report-builder-field-item').forEach((item) => {
      const allowed = grounded.has(item.dataset.fieldPath);
      item.classList.toggle('disabled', !allowed);
      item.setAttribute('aria-disabled', (!allowed).toString());
      item.title = allowed ? '' : 'Group by this record (its id) first to display its fields';
    });
  }

  submitForm(_event) {
    this.hiddenColumnsConfigTarget.innerHTML = '';
    let colIndex = 0;

    // Detail reports: selected column rows from the two-panel table.
    this.selectedColumnRowTargets.forEach((row) => {
      this.serializeDetailColumn(row, colIndex);
      colIndex += 1;
    });

    // Summary reports: grouping cards (group keys + display fields).
    this.groupingCardTargets.forEach((card) => {
      this.serializeGroupingCard(card, colIndex);
      colIndex += 1;
    });
  }

  serializeDetailColumn(row, index) {
    const colName = row.dataset.columnName;
    const table = row.dataset.columnTable;
    const association = row.dataset.columnAssociation;
    const type = row.dataset.columnType;
    const isCalculated = row.dataset.isCalculated === 'true' || type === 'calculated' || table === 'calculated';
    const relationship = row.dataset.columnRelationship;
    const isRelationship = type === 'relationship' && relationship;

    const labelInput = row.querySelector('input[type="text"]');
    const customLabel = labelInput ? labelInput.value : (row.dataset.columnLabel || this.generateLabel(colName));
    const formula = this.buildFormulaForRow(row, isCalculated);
    const sortControl = row.querySelector('.report-builder-sort-control');
    const isSortable = sortControl ? sortControl.checked : false;

    this.createHiddenInput(`rad_report[columns][${index}][name]`, colName);
    this.createHiddenInput(`rad_report[columns][${index}][label]`, customLabel);

    if (isCalculated) {
      this.createHiddenInput(`rad_report[columns][${index}][is_calculated]`, '1');
    } else if (type !== 'rich_text') {
      const selectPrefix = association && association !== '' ? association : table;
      this.createHiddenInput(`rad_report[columns][${index}][select]`, `${selectPrefix}.${colName}`);
    }

    if (isRelationship) {
      const relationshipPath = association && association !== '' ? `${association}.${relationship}` : relationship;
      this.createHiddenInput(`rad_report[columns][${index}][relationship]`, relationshipPath);
    }

    if (formula) {
      this.createHiddenInput(`rad_report[columns][${index}][formula]`, formula);
    }

    this.createHiddenInput(`rad_report[columns][${index}][sortable]`, isSortable ? '1' : '0');
  }

  serializeGroupingCard(card, index) {
    const labelInput = card.querySelector('input[type="text"]');
    const label = labelInput ? labelInput.value : card.dataset.columnLabel;
    const isCalculated = card.dataset.columnIsCalculated === 'true';

    this.createHiddenInput(`rad_report[columns][${index}][name]`, card.dataset.columnName);
    this.createHiddenInput(`rad_report[columns][${index}][label]`, label);

    if (isCalculated) {
      // Calculated display fields have no select clause; they are never a group key.
      this.createHiddenInput(`rad_report[columns][${index}][is_calculated]`, '1');
    } else {
      this.createHiddenInput(`rad_report[columns][${index}][select]`, card.dataset.columnSelect);
      this.createHiddenInput(`rad_report[columns][${index}][group_key]`, card.dataset.columnGroupKey === 'true' ? '1' : '0');
    }

    this.createHiddenInput(`rad_report[columns][${index}][hidden]`, card.dataset.columnHidden === 'true' ? '1' : '0');

    const sortControl = card.querySelector('.report-builder-sort-control');
    this.createHiddenInput(`rad_report[columns][${index}][sortable]`, sortControl && sortControl.checked ? '1' : '0');

    const formula = this.buildFormulaForRow(card, isCalculated);
    if (formula) {
      this.createHiddenInput(`rad_report[columns][${index}][formula]`, formula);
    }
  }

  buildFormulaForRow(row, isCalculated) {
    const transforms = row.dataset.columnFormula || '';

    if (isCalculated) {
      const calculation = row.dataset.columnCalculation || '';
      const calcObj = calculation ? this.parseJson(calculation) : null;
      const transformsArr = transforms ? this.parseJson(transforms) : [];

      if (calcObj) {
        const combined = [calcObj, ...(Array.isArray(transformsArr) ? transformsArr : [])];
        return JSON.stringify(combined);
      }
    }

    return transforms;
  }

  parseJson(str) {
    if (!str) return null;
    try {
      return JSON.parse(str);
    } catch {
      return null;
    }
  }


  createHiddenInput(name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    this.hiddenColumnsConfigTarget.appendChild(input);
  }

  loadCalculatedColumnForm(event) {
    const url = event.currentTarget.dataset.calculatedColumnUrl;
    if (!url) {
      return;
    }

    const refreshedUrl = new URL(url, window.location.origin);
    refreshedUrl.searchParams.set('_', Date.now().toString());

    const frameId = this.hasCalculatedColumnFrameTarget
      ? this.calculatedColumnFrameTarget.id
      : 'calculated-column-form-frame';

    Turbo.visit(refreshedUrl.toString(), { frame: frameId });
  }

  editCalculatedColumn(event) {
    const url = event.currentTarget.dataset.calculatedColumnUrl;
    if (!url) {
      return;
    }

    const refreshedUrl = new URL(url, window.location.origin);
    refreshedUrl.searchParams.set('_', Date.now().toString());

    const row = event.currentTarget.closest('tr, .grouping-card');
    if (row) {
      refreshedUrl.searchParams.set('row_id', row.id);
    }

    const frameId = this.hasCalculatedColumnFrameTarget
      ? this.calculatedColumnFrameTarget.id
      : 'calculated-column-form-frame';

    Turbo.visit(refreshedUrl.toString(), { frame: frameId });
  }

  // Loads the field list for a relationship-tree node into the shared "Add Fields" modal.
  loadTreeFields(event) {
    const url = event.currentTarget.dataset.treeFieldsUrl;
    if (!url) {
      return;
    }

    const refreshedUrl = new URL(url, window.location.origin);
    refreshedUrl.searchParams.set('_', Date.now().toString());

    Turbo.visit(refreshedUrl.toString(), { frame: 'relationship-fields-frame' });
  }

  // A related table only joins the report once one of its fields is added. When that happens we drop a
  // hidden rad_report[joins][] input (one per ancestor path) so the join persists on save. Deduped
  // by value so re-adding fields from the same table is a no-op.
  registerJoin(event) {
    const path = event.currentTarget.dataset.joinPath;
    if (!path || !this.hasJoinsConfigTarget) {
      return;
    }

    const container = this.joinsConfigTarget;
    const existing = new Set(
      Array.from(container.querySelectorAll('input[name="rad_report[joins][]"]')).map((input) => input.value)
    );

    const parts = path.split('.');
    parts.forEach((_part, index) => {
      const ancestorPath = parts.slice(0, index + 1).join('.');
      if (existing.has(ancestorPath)) {
        return;
      }
      existing.add(ancestorPath);
      this.appendJoinInput(container, ancestorPath);
    });
  }

  appendJoinInput(container, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'rad_report[joins][]';
    input.value = value;
    container.appendChild(input);
  }

  // "Add filter" on a tree field: close the field picker, switch to the Filters tab, and open the filter
  // dialog with this column preselected. Current joins are passed so a related column resolves there.
  addFilterForField(event) {
    const btn = event.currentTarget;
    const baseUrl = btn.dataset.filterFormUrl;
    if (!baseUrl) {
      return;
    }

    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('column', btn.dataset.filterColumn);

    const joins = new Set(this.currentJoinPaths());
    const joinPath = btn.dataset.joinPath;
    if (joinPath) {
      const parts = joinPath.split('.');
      parts.forEach((_part, index) => joins.add(parts.slice(0, index + 1).join('.')));
    }
    joins.forEach((join) => url.searchParams.append('joins[]', join));
    url.searchParams.set('_', Date.now().toString());

    const openFilterDialog = () => {
      this.activateTab('filters');
      bootstrap.Modal.getOrCreateInstance(document.getElementById('rad-report-filter-modal')).show();
      Turbo.visit(url.toString(), { frame: 'rad-report-filter-form-frame' });
    };

    const fieldsModalEl = document.getElementById('relationship-fields-modal');
    if (fieldsModalEl && fieldsModalEl.classList.contains('show')) {
      fieldsModalEl.addEventListener('hidden.bs.modal', openFilterDialog, { once: true });
      bootstrap.Modal.getOrCreateInstance(fieldsModalEl).hide();
    } else {
      openFilterDialog();
    }
  }

  currentJoinPaths() {
    if (!this.hasJoinsConfigTarget) {
      return [];
    }
    return Array.from(this.joinsConfigTarget.querySelectorAll('input[name="rad_report[joins][]"]'))
      .map((input) => input.value);
  }

  activateTab(tabId) {
    const link = this.element.querySelector(`#tab-${tabId}-tab`);
    if (link) {
      bootstrap.Tab.getOrCreateInstance(link).show();
    }
  }

  loadRadReportFilterForm(event) {
    const url = event.currentTarget.dataset.radReportFilterUrl;
    if (!url) {
      return;
    }

    const refreshedUrl = new URL(url, window.location.origin);
    this.applyCurrentJoins(refreshedUrl);
    refreshedUrl.searchParams.set('_', Date.now().toString());

    Turbo.visit(refreshedUrl.toString(), { frame: 'rad-report-filter-form-frame' });
  }

  loadRadReportAggregateForm(event) {
    const url = event.currentTarget.dataset.radReportAggregateUrl;
    if (!url) {
      return;
    }

    const refreshedUrl = new URL(url, window.location.origin);
    this.applyCurrentJoins(refreshedUrl);
    refreshedUrl.searchParams.set('_', Date.now().toString());

    Turbo.visit(refreshedUrl.toString(), { frame: 'rad-report-aggregate-form-frame' });
  }

  // Replaces the joins baked into the URL at page load with the joins currently in the builder, so
  // related-model fields added but not yet saved appear in the form's column dropdown.
  applyCurrentJoins(url) {
    url.searchParams.delete('joins[]');
    this.currentJoinPaths().forEach((join) => url.searchParams.append('joins[]', join));
  }

  // Loads the calculated-measure form, passing the plain measures currently in the builder (including
  // unsaved ones) so they can be picked as operands. Their aliases mirror the server's result_alias
  // (`<function>_<column>` with dots replaced by underscores).
  loadCalculatedMeasureForm(event) {
    const url = event.currentTarget.dataset.calculatedMeasureUrl;
    if (!url) {
      return;
    }

    const refreshedUrl = new URL(url, window.location.origin);
    refreshedUrl.searchParams.set('_', Date.now().toString());

    this.currentMeasures().forEach((measure) => {
      refreshedUrl.searchParams.append('measures[][alias]', measure.alias);
      refreshedUrl.searchParams.append('measures[][label]', measure.label);
    });

    Turbo.visit(refreshedUrl.toString(), { frame: 'calculated-measure-form-frame' });
  }

  currentMeasures() {
    return this.aggregateRowTargets.flatMap((row) => {
      if (row.querySelector('input[name="rad_report[aggregates][][is_calculated]"]')) return [];

      const column = row.querySelector('input[name="rad_report[aggregates][][column]"]')?.value;
      const fn = row.querySelector('input[name="rad_report[aggregates][][function]"]')?.value;
      const label = row.querySelector('input[name="rad_report[aggregates][][label]"]')?.value;
      if (!column || !fn) return [];

      const alias = `${fn}_${column}`.replace(/\./g, '_');
      return [{ alias, label: label || alias }];
    });
  }

  removeColumn(event) {
    event.currentTarget.closest('tr')?.remove();
  }

  removeFilter(event) {
    event.currentTarget.closest('.filter-row')?.remove();
  }

  removeAggregate(event) {
    event.currentTarget.closest('.aggregate-row')?.remove();
  }

  generateLabel(colName) {
    return colName.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
  }
}
